import {DragDropModule} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule as AngularFormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {ImagePlaceholderModule} from '@app/components/images/image-placeholder.module';
import {ValueComponent} from '@app/forms/primitives/value/value.component';
import {SharedModule} from '@app/shared/shared.module';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {DatepickerComponent} from '@forms/primitives/datepicker/datepicker.component';
import {EnumComponent} from '@forms/primitives/enum/enum.component';
import {SliderFormComponent} from '@forms/primitives/slider/slider-form.component';
import {TickComponent} from '@forms/primitives/tick/tick.component';
import {FormattedDateTimeInputComponent} from '@forms/primitives/value/formatted-date-time-input.component';
import {IconValueComponent} from '@forms/primitives/value/icon-value.component';
import {JsonValueComponent} from '@forms/primitives/value/json-value.component';
import {LabelledLinkValueComponent} from '@forms/primitives/value/labelled-link-value.component';
import {LabelledRouterLinkValueComponent} from '@forms/primitives/value/labelled-router-link-value.component';
import {LinkValueComponent} from '@forms/primitives/value/link-value.component';
import {MappedValueComponent} from '@forms/primitives/value/mapped-value.component';
import {MoneyValueComponent} from '@forms/primitives/value/money-value.component';
import {PercentValueComponent} from '@forms/primitives/value/percent-value.component';
import {StartCaseValueComponent} from '@forms/primitives/value/start-case-value.component';
import {UpperCaseValueComponent} from '@forms/primitives/value/upper-case-value.component';
import {WarningComponent} from '@forms/primitives/warning/warning.component';
import {fieldDateRangeValidator, fieldMatchValidator, minlengthValidationMessages} from '@forms/validators';
import {ClickableWrapperComponent} from '@forms/wrappers/clickable-wrapper/clickable-wrapper.component';
import {StyleWrapperComponent} from '@forms/wrappers/style-wrapper/style-wrapper.component';
import {FormlyZzzingWrapperFormFieldComponent} from '@forms/wrappers/zzzing-form-field-wrapper/zzzing-form-field-wrapper.component';
import {NgSelectModule} from '@ng-select/ng-select';
import {FormlyBootstrapModule} from '@ngx-formly/bootstrap';
import {FormlyModule} from '@ngx-formly/core';
import {FormlySelectModule} from '@ngx-formly/core/select';
import * as moment from 'moment';
import {NouisliderModule} from 'ng2-nouislider';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {PerfectScrollbarModule} from 'ngx-perfect-scrollbar';
import {ArrayComponent} from './primitives/array/array.component';
import {CoordinatesComponent} from './primitives/coordinates/coordinates.component';
import {ImageComponent} from './primitives/image/image.component';
import {NullComponent} from './primitives/null/null.component';
import {ObjectComponent} from './primitives/object/object.component';
import {TextAreaComponent} from './primitives/text-area/text-area.component';
import {TypeheadComponent} from './primitives/typehead/typehead.component';
import {CardWrapperComponent} from './wrappers/card-wrapper/card-wrapper.component';
import {RoundProgressModule} from 'angular-svg-round-progressbar';

export function toDateParser(date: Date) {
  return date ? moment(date).format('YYYY-MM-DD') : null;
}

@NgModule({
  declarations: [
    ObjectComponent,
    ArrayComponent,
    NullComponent,
    CoordinatesComponent,
    ImageComponent,
    TextAreaComponent,
    CardWrapperComponent,
    TypeheadComponent,
    StyleWrapperComponent,
    TickComponent,
    IconValueComponent,
    JsonValueComponent,
    LinkValueComponent,
    PercentValueComponent,
    LabelledLinkValueComponent,
    LabelledRouterLinkValueComponent,
    MoneyValueComponent,
    StartCaseValueComponent,
    UpperCaseValueComponent,
    MappedValueComponent,
    ValueComponent,
    WarningComponent,
    SliderFormComponent,
    DatepickerComponent,
    FormattedDateTimeInputComponent,
    EnumComponent,
    FormlyZzzingWrapperFormFieldComponent
  ],
  providers: [],
  imports: [
    SharedModule,
    CommonModule,
    DragDropModule,
    AngularFormsModule,
    ReactiveFormsModule,
    LeafletModule,
    RouterModule,
    SharedModule,
    FormlyModule.forRoot({
      extras: {checkExpressionOn: 'modelChange'},
      validators: [
        {name: 'fieldMatch', validation: fieldMatchValidator},
        {name: 'dateRange', validation: fieldDateRangeValidator},
      ],
      validationMessages: [
        {name: 'required', message: 'This field is required'},
        {name: 'minlength', message: minlengthValidationMessages},
        {name: 'min', message: 'This field exceeds the minimum'},
        {name: 'max', message: 'This field exceeds the maximum'}
      ],
      wrappers: [
        {name: 'card', component: CardWrapperComponent},
        {name: 'clickable', component: ClickableWrapperComponent},
        {name: 'style', component: StyleWrapperComponent},
        {name: 'zzzing-form-field-wrapper', component: FormlyZzzingWrapperFormFieldComponent},
      ],
      extensions: [],
      types: [
        {name: 'string', extends: 'input', wrappers: ['zzzing-form-field-wrapper']},
        {name: 'formatted-date-time-input', component: FormattedDateTimeInputComponent},
        {
          name: 'date',
          component: DatepickerComponent,
          wrappers: ['zzzing-form-field-wrapper'],
          defaultOptions: {
            templateOptions: {
              hideLabel: true // Hides field wrapper label, but datepicker label is still shown
            }
          }
        },
        {name: 'textarea', component: TextAreaComponent},
        {name: 'value', component: ValueComponent},
        {name: 'money-value', component: MoneyValueComponent},
        {name: 'json-value', component: JsonValueComponent},
        {name: 'link-value', component: LinkValueComponent},
        {name: 'percent-value', component: PercentValueComponent},
        {name: 'labelled-link', component: LabelledLinkValueComponent},
        {name: 'labelled-router-link', component: LabelledRouterLinkValueComponent},
        {name: 'icon-value', component: IconValueComponent},
        {name: 'start-case-value', component: StartCaseValueComponent},
        {name: 'upper-case-value', component: UpperCaseValueComponent},
        {name: 'mapped-value', component: MappedValueComponent},
        {name: 'warning', component: WarningComponent},
        {
          name: 'number',
          extends: 'input',
          wrappers: ['zzzing-form-field-wrapper'],
          defaultOptions: {
            templateOptions: {
              type: 'number',
            },
          },
        },
        {
          name: 'integer',
          extends: 'input',
          wrappers: ['zzzing-form-field-wrapper'],
          defaultOptions: {
            className: 'expand',
            templateOptions: {
              type: 'number',
            },
          },
        },
        {name: 'boolean', extends: 'checkbox'},
        {name: 'enum', component: EnumComponent, wrappers: ['zzzing-form-field-wrapper']},
        {name: 'z_select', extends: 'select', wrappers: ['zzzing-form-field-wrapper']},
        {name: 'slider', component: SliderFormComponent},
        {name: 'null', component: NullComponent, wrappers: ['form-field']},
        {name: 'array', component: ArrayComponent},
        {name: 'object', component: ObjectComponent},
        {name: 'image', component: ImageComponent},
        {name: 'tick', component: TickComponent},
        {name: 'typehead', component: TypeheadComponent, wrappers: ['zzzing-form-field-wrapper']},
        {
          name: 'coordinates',
          wrappers: ['form-field'],
          component: CoordinatesComponent,
        },
      ]
    }),
    FormlySelectModule,
    FormlyBootstrapModule,
    NgSelectModule,
    PerfectScrollbarModule,
    NouisliderModule,
    BsDatepickerModule,
    ImagePlaceholderModule,
    RoundProgressModule
  ],
  exports: [
    ReactiveFormsModule,
    FormlyBootstrapModule,
    FormlyModule]
})
export class FormsModule {
}
